import { useCallback, useEffect, useMemo, useState } from "react";
import {
  COMMENT_SCORE_OPTIONS,
  GOOD_SCORE_OPTIONS,
  MISS_SCORE_OPTIONS,
  STRUCTURE_SCORE_OPTIONS,
  calcRank,
  calcTotal,
} from "../../shared/scoring";
import columnMap from "../../shared/column-map.json";
import { getEvaluation, getEvaluations, logout, saveEvaluation } from "../lib/api";
import type {
  EvalueeListItem,
  EvaluationDetail,
  EvaluationsListResponse,
  PublicConfig,
  SaveEvaluationBody,
  SaveMode,
} from "../lib/types";
import StatusBadge from "../components/StatusBadge";
import { useToast } from "../components/Toast";

type Props = {
  evaluatorName: string;
  config: PublicConfig | null;
  onLogout: () => void | Promise<void>;
  onSwitchToAdmin?: () => void;
};

const DONE_STATUS = columnMap.statusValues.done;

export default function EvaluatorPortal({ evaluatorName, config, onLogout, onSwitchToAdmin }: Props) {
  const { showToast } = useToast();

  const [listData, setListData] = useState<EvaluationsListResponse | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [detail, setDetail] = useState<EvaluationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [commentScore, setCommentScore] = useState<number | null>(null);
  const [missScore, setMissScore] = useState<number | null>(null);
  const [goodScore, setGoodScore] = useState<number | null>(null);
  const [structureScore, setStructureScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");

  const [saveError, setSaveError] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [savingDone, setSavingDone] = useState(false);

  const isClosed = config?.status === "closed";

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    const res = await getEvaluations();
    setListLoading(false);
    if (res.ok) {
      setListData(res.data);
    } else {
      setListError(res.error);
    }
    return res;
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const selectEvaluee = useCallback(async (rowId: number) => {
    setSelectedRowId(rowId);
    setDetailLoading(true);
    setDetailError(null);
    setSaveError(null);
    const res = await getEvaluation(rowId);
    setDetailLoading(false);
    if (res.ok) {
      setDetail(res.data);
      setCommentScore(res.data.scores.commentScore);
      setMissScore(res.data.scores.missScore);
      setGoodScore(res.data.scores.goodScore);
      setStructureScore(res.data.scores.structureScore);
      setComment(res.data.comment ?? "");
    } else {
      setDetail(null);
      setDetailError(res.error);
    }
  }, []);

  const returnScore = detail?.returnScore ?? 0;
  const previewTotal = useMemo(
    () =>
      calcTotal({
        returnScore,
        commentScore: commentScore ?? 0,
        missScore: missScore ?? 0,
        goodScore: goodScore ?? 0,
        structureScore: structureScore ?? 0,
      }),
    [returnScore, commentScore, missScore, goodScore, structureScore]
  );
  const previewRank = useMemo(() => calcRank(previewTotal), [previewTotal]);

  const commentEmpty = comment.trim().length === 0;
  const scoresIncomplete =
    commentScore === null || missScore === null || goodScore === null || structureScore === null;

  const disableReasons: string[] = [];
  if (isClosed) disableReasons.push("受付は終了しています");
  if (commentEmpty) disableReasons.push("コメントを入力してください");
  if (scoresIncomplete) disableReasons.push("4項目すべてのスコアを選択してください");

  const doneDisabled = !detail || disableReasons.length > 0 || savingDraft || savingDone;
  const draftDisabled = !detail || isClosed || savingDraft || savingDone;

  function buildBody(mode: SaveMode): SaveEvaluationBody {
    const body: SaveEvaluationBody = { mode, comment };
    if (commentScore !== null) body.commentScore = commentScore;
    if (missScore !== null) body.missScore = missScore;
    if (goodScore !== null) body.goodScore = goodScore;
    if (structureScore !== null) body.structureScore = structureScore;
    return body;
  }

  function advanceToNext(justSavedRowId: number, evaluees: EvalueeListItem[]) {
    if (evaluees.length === 0) return;
    const currentIndex = evaluees.findIndex((e) => e.rowId === justSavedRowId);
    if (currentIndex === -1) return;
    let nextIndex = -1;
    for (let offset = 1; offset <= evaluees.length; offset++) {
      const idx = (currentIndex + offset) % evaluees.length;
      if (evaluees[idx].status !== DONE_STATUS) {
        nextIndex = idx;
        break;
      }
    }
    if (nextIndex === -1) {
      nextIndex = (currentIndex + 1) % evaluees.length;
    }
    selectEvaluee(evaluees[nextIndex].rowId);
  }

  async function handleSaveDraft() {
    if (!detail) return;
    setSaveError(null);
    setSavingDraft(true);
    const res = await saveEvaluation(detail.rowId, buildBody("draft"));
    setSavingDraft(false);
    if (!res.ok) {
      setSaveError(res.error);
      return;
    }
    showToast("一時保存しました", "success");
    await loadList();
    await selectEvaluee(detail.rowId);
  }

  async function handleSaveDone() {
    if (!detail) return;
    setSaveError(null);
    setSavingDone(true);
    const rowId = detail.rowId;
    const res = await saveEvaluation(rowId, buildBody("done"));
    setSavingDone(false);
    if (!res.ok) {
      setSaveError(res.error);
      return;
    }
    showToast("保存しました。次の対象者に進みます", "success");
    const listRes = await loadList();
    if (listRes.ok) {
      advanceToNext(rowId, listRes.data.evaluees);
    }
  }

  async function handleLogout() {
    await logout();
    await onLogout();
  }

  return (
    <div className="evaluator-portal">
      <header className="portal-header">
        <div className="portal-header__titles">
          <span className="portal-title">評価者ポータル</span>
          <span className="portal-subtitle">{evaluatorName} さん</span>
        </div>
        <div className="portal-header__actions">
          {onSwitchToAdmin && (
            <button type="button" className="btn btn--ghost" onClick={onSwitchToAdmin}>
              管理者ポータルへ切替
            </button>
          )}
          <button type="button" className="btn btn--ghost" onClick={handleLogout}>
            ログアウト
          </button>
        </div>
      </header>

      {isClosed && <div className="closed-banner">受付は終了しています</div>}

      <div className="evaluator-columns">
        <aside className="column column--list">
          <div className="counts-row">
            <div className="count-tile">
              <span className="count-tile__label">担当件数</span>
              <span className="count-tile__value">{listData?.counts.assigned ?? "-"}</span>
            </div>
            <div className="count-tile">
              <span className="count-tile__label">評価済み件数</span>
              <span className="count-tile__value">{listData?.counts.done ?? "-"}</span>
            </div>
            <div className="count-tile">
              <span className="count-tile__label">未評価件数</span>
              <span className="count-tile__value">{listData?.counts.remaining ?? "-"}</span>
            </div>
          </div>

          {listLoading && <p className="hint-text">読み込み中...</p>}
          {listError && <p className="error-text">{listError}</p>}

          <ul className="evaluee-list">
            {listData?.evaluees.map((item) => (
              <li key={item.rowId}>
                <button
                  type="button"
                  className={`evaluee-item ${selectedRowId === item.rowId ? "evaluee-item--active" : ""}`}
                  onClick={() => selectEvaluee(item.rowId)}
                >
                  <span className="evaluee-item__id">{item.id}</span>
                  <span className="evaluee-item__name">{item.name}</span>
                  <StatusBadge status={item.status} />
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="column column--detail">
          {!detail && !detailLoading && (
            <div className="empty-state">左の一覧から評価対象者を選択してください</div>
          )}
          {detailLoading && <div className="empty-state">読み込み中...</div>}
          {detailError && <p className="error-text">{detailError}</p>}
          {detail && !detailLoading && (
            <div className="evaluee-card">
              <dl className="evaluee-info">
                <div>
                  <dt>ID</dt>
                  <dd>{detail.id}</dd>
                </div>
                <div>
                  <dt>氏名</dt>
                  <dd>{detail.name}</dd>
                </div>
                <div>
                  <dt>担当者</dt>
                  <dd>{detail.evaluator}</dd>
                </div>
                <div>
                  <dt>ステータス</dt>
                  <dd>
                    <StatusBadge status={detail.status} />
                  </dd>
                </div>
              </dl>

              <h3 className="section-heading">対象答案PDF</h3>
              {detail.pdfLinks.length === 0 ? (
                <p className="hint-text">PDFが登録されていません</p>
              ) : (
                <div className="pdf-link-grid">
                  {detail.pdfLinks.map((link) => (
                    <a key={link.url} href={link.url} target="_blank" rel="noreferrer" className="pdf-link-card">
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        <section className="column column--form">
          {!detail ? (
            <div className="empty-state">対象者を選択すると評価を入力できます</div>
          ) : (
            <div className="score-form">
              <div className="score-readonly">
                <span className="score-readonly__label">差し戻し点(自動計算・編集不可)</span>
                <span className="score-readonly__value">{detail.returnScore ?? "-"}</span>
              </div>
              <ScoreOptionGroup
                label="コメント点"
                options={COMMENT_SCORE_OPTIONS}
                value={commentScore}
                onChange={setCommentScore}
                disabled={isClosed}
              />
              <ScoreOptionGroup
                label="ミス点"
                options={MISS_SCORE_OPTIONS}
                value={missScore}
                onChange={setMissScore}
                disabled={isClosed}
              />
              <ScoreOptionGroup
                label="良かった点"
                options={GOOD_SCORE_OPTIONS}
                value={goodScore}
                onChange={setGoodScore}
                disabled={isClosed}
              />
              <ScoreOptionGroup
                label="構成点"
                options={STRUCTURE_SCORE_OPTIONS}
                value={structureScore}
                onChange={setStructureScore}
                disabled={isClosed}
              />

              <label className="comment-field">
                <span>コメント</span>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={6}
                  disabled={isClosed}
                  placeholder="総合コメントを入力してください"
                />
              </label>

              <div className="score-preview">
                <div>
                  <span className="score-preview__label">合計点</span>
                  <span className="score-preview__value">{previewTotal}</span>
                </div>
                <div>
                  <span className="score-preview__label">評価ランク</span>
                  <span className={`score-preview__value score-preview__value--rank rank-${previewRank}`}>
                    {previewRank}
                  </span>
                </div>
              </div>

              {saveError && <p className="error-text">{saveError}</p>}

              <div className="save-buttons">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={handleSaveDraft}
                  disabled={draftDisabled}
                  title={isClosed ? "受付は終了しています" : undefined}
                >
                  {savingDraft ? "保存中..." : "一時保存"}
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleSaveDone}
                  disabled={doneDisabled}
                  title={disableReasons.length > 0 ? disableReasons.join(" / ") : undefined}
                >
                  {savingDone ? "保存中..." : "保存して次の対象者へ"}
                </button>
              </div>
              {doneDisabled && disableReasons.length > 0 && (
                <p className="hint-text hint-text--warn">{disableReasons.join(" / ")}</p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

type ScoreOptionGroupProps = {
  label: string;
  options: readonly number[];
  value: number | null;
  onChange: (value: number) => void;
  disabled?: boolean;
};

function ScoreOptionGroup({ label, options, value, onChange, disabled }: ScoreOptionGroupProps) {
  return (
    <div className="score-option-group">
      <span className="score-option-group__label">{label}</span>
      <div className="score-option-group__buttons">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`score-option ${value === opt ? "score-option--active" : ""}`}
            onClick={() => onChange(opt)}
            disabled={disabled}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}
